/**
 * app/javascript/controllers/berth_path_controller.js
 *
 * A specialized Stimulus controller that manages individual berth elements within marina
 * berth maps. This controller is programmatically created and managed by BerthMapController
 * for each berth shape in the SVG map, handling user interactions, visual styling, boat
 * positioning, and integration with the marina management interface. It applies dynamic
 * styling based on berth availability (free, occupied by transit/rental/owner boats),
 * manages selection states with visual feedback, and creates contextual tooltips.
 *
 * The controller includes sophisticated geometry handling for boat icon positioning within
 * berths, supporting both SVG path and rect elements with complex transformations for
 * proper scaling, rotation, and alignment. It coordinates with the parent BerthMapController
 * for data updates and selection events, integrates with sidebar panels for detailed berth
 * information, and provides full keyboard navigation and accessibility support.
 *
 * @see {@link https://www.notion.so/metarina/BerthPathController-Documentation-23a0bdb0debb803a923ad70299c85ffe?source=copy_link} Complete Documentation
 */

/* eslint-disable no-unused-expressions */
import { Controller } from "stimulus";
import tippy from "tippy.js";
import "path-data-polyfill";
import { SidebarService } from "./shared/sidebar_service";
import { BerthDisplayVariants } from "./shared/slots/berth_display_variants";

const NODE_ENV = import.meta.env.MODE || "production";
const shouldLog = NODE_ENV === "development";

export default class extends Controller {
  static targets = ["rect", "boat", "boatLayer"];

  static values = {
    berth: Object,
    mapCalendarEntry: Object,
    selected: Boolean,
    date: String,
  };

  connect() {
    this.tooltipInitialized = false;
    this.initDOM();
    document.addEventListener("keydown", this.handleKeyDown);

    this.debouncedInitDOM = this.constructor.debounce(this.initDOM.bind(this), 50);
  }

  get displayVariant() {
    return this.selectedValue
      ? "selected"
      : (this.mapCalendarEntryValue.display_variant || "free_berth");
  }

  disconnect() {
    this.unmountTooltip();
    this.tooltipInitialized = false;
    document.removeEventListener("keydown", this.handleKeyDown);

    if (this.debouncedInitDOM && this.debouncedInitDOM.timeout) {
      clearTimeout(this.debouncedInitDOM.timeout);
    }
  }

  berthValueChanged() {
    if (this.debouncedInitDOM) {
      this.debouncedInitDOM();
    } else {
      this.initDOM();
    }
  }

  mapCalendarEntryValueChanged() {
    if (this.debouncedInitDOM) {
      this.debouncedInitDOM();
    } else {
      this.initDOM();
    }
  }

  dateValueChanged() {
    if (this.debouncedInitDOM) {
      this.debouncedInitDOM();
    } else {
      this.initDOM();
    }
  }

  selectedValueChanged() {
    if (this.debouncedInitDOM) {
      this.debouncedInitDOM();
    } else {
      this.initDOM();
    }

    if (this.selectedValue) {
      this.initSidebar();
    }
  }

  handleClick(event) {
    event.preventDefault();

    // Open sidebar using shared service
    SidebarService.openBerthSidebar({
      date: this.dateValue,
      berthId: this.berthValue.id,
      slotId: this.mapCalendarEntryValue?.id,
      application: this.application, // Pass the application instance
    });

    // Apply visual selection styling
    this.unselectAllBerths();
    this.select();
  }

  handleKeyDown = (event) => {
    if (document.activeElement !== this.element) return;

    if (event.key === "Enter") {
      this.handleClick(event);
    }
  };

  handleMouseEnter = () => {
    // Only create tooltip on first hover
    if (!this.tooltipInitialized) {
      this.initTooltip();
      this.tooltipInitialized = true;

      // Manually trigger tooltip show since we created it after hover started
      if (this.tippyInstance) {
        this.tippyInstance.show();
      }
    }
  };

  handleTouchStart = () => {
    // Create tooltip on touch for mobile devices
    if (!this.tooltipInitialized) {
      this.initTooltip();
      this.tooltipInitialized = true;
    }
  };

  static debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // private

  initDOM() {
    // Only unmount tooltip if it was already created
    if (this.tooltipInitialized) {
      this.unmountTooltip();
      this.tooltipInitialized = false;
    }

    this.initInteractivity();
    this.initRect();
    this.initBoat();
  }

  initInteractivity() {
    this.element.dataset.action = "pointerdown->berth-path#handleClick click->berth-path#handleClick mouseenter->berth-path#handleMouseEnter touchstart->berth-path#handleTouchStart";
    this.element.tabIndex = "0";
    this.element.ariaLabel = this.screenReaderLabel;
    this.element.classList = this.className;
    this.element.ariaSelected = this.selectedValue;

    if (!this.path) {
      throw new Error(
        "No path found on the page. You must have a rect on the page to use the berth path feature.",
      );
    }
    if (shouldLog) {
      this.element.dataset.valueLogs = JSON.stringify({
        x: this.x,
        y: this.y,
        width: this.width,
        height: this.height,
        angle: this.angle,
        isPath: this.isPath,
      });
    }
  }

  initTooltip() {
    this.unmountTooltip();

    // Get i18n data from parent berth-map controller
    const berthMapController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="berth-map"]'),
      "berth-map",
    );

    // Use existing individual tooltip data attributes (maintains backward compatibility)
    const berthMapElement = document.querySelector('[data-controller*="berth-map"]');
    if (!berthMapElement) {
      console.warn("berth-map element not found for tooltip initialization");
      return;
    }

    const variantDescriptions = berthMapController?.variantDescriptionsValue || {};

    const berthTypeDescription = BerthDisplayVariants.getVariantDescription(
      this.displayVariant,
      variantDescriptions,
    );

    const { berthValue } = this;
    const tooltipContentTemplate = berthMapElement.dataset.berthMapTooltipContent || "%{pontoon} %{position_number} - %{type}";

    const tooltipContent = tooltipContentTemplate
      .replace("%{pontoon}", berthValue.pontoon)
      .replace("%{position_number}", berthValue.pontoon_number)
      .replace("%{type}", berthTypeDescription);

    this.tippyInstance = tippy(this.element, {
      content: tooltipContent,
      touch: false,
    });
  }

  unmountTooltip() {
    if (this.tippyInstance) {
      this.tippyInstance.destroy();
      this.tippyInstance = null;
    }
    this.tooltipInitialized = false;
  }

  initRect() {
    // replace the default path with a real rect element
    if (this.isPath) this.path.classList = "fill-none hidden-path cursor-pointer";

    const path = this.isPath
      ? document.createElementNS("http://www.w3.org/2000/svg", "rect")
      : this.path;
    const el = this.hasRectTarget ? this.rectTarget : path;

    if (this.isPath) {
      el.setAttribute("x", this.x);
      el.setAttribute("y", this.y);
      el.setAttribute("width", this.width);
      el.setAttribute("height", this.height);
      el.setAttribute("transform", `rotate(${this.angle} ${this.x} ${this.y})`);
    }

    // this sets the rounded corners of the rect
    el.setAttribute("rx", "1.5");
    el.setAttribute("ry", "1.5");
    el.dataset.berthPathTarget = "rect";

    // Use BerthDisplayVariants service instead of manual switch statement
    BerthDisplayVariants.applySVGStyles(el, this.displayVariant);

    if (!el.isConnected) this.element.insertAdjacentElement("beforeend", el);
  }

  initSidebar() {
    if (!this.selectedValue) return;

    // Open sidebar using shared service for selected berths
    SidebarService.openBerthSidebar({
      date: this.dateValue,
      berthId: this.berthValue.id,
      slotId: this.mapCalendarEntryValue?.id,
      application: this.application, // Pass the application instance
    });
  }

  initBoat() {
    if (!this.boatIconTarget) {
      throw new Error(
        "No boat icon SVG found on the page. You must have a boat icon SVG on the page to use the boat feature.",
      );
    }

    if (!this.shouldShowBoat) {
      this.rectTarget.classList = "";
      this.hasBoatLayerTarget && this.boatLayerTarget.remove();
      return this.hasBoatTarget && this.boatTarget.remove();
    }

    this.rectTarget.classList = "fill-none hidden-path cursor-pointer";

    const el = this.hasBoatTarget
      ? this.boatTarget
      : this.boatIconTarget.cloneNode(true);
    if (!this.hasBoatTarget) {
      // make a new layer `<g>` with this `<path>` in it
      const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
      g.dataset.berthPathTarget = "boatLayer";
      const g2 = document.createElementNS("http://www.w3.org/2000/svg", "g");
      this.element.insertAdjacentElement("beforeend", g);
      g.insertAdjacentElement("beforeend", g2);
      g2.insertAdjacentElement("beforeend", el);
    }

    el.dataset.berthPathTarget = "boat";
    el.dataset.berthMapTarget = "";
    el.setAttribute("class", BerthDisplayVariants.getCSSClasses(this.displayVariant, "map"));

    const elBBox = el.getBBox();
    const flipped = /flipped/.test(this.element.id);
    const scaleX = this.shortSide / elBBox.width;
    const scaleY = ((flipped ? -1 : 1) * this.boatHeight) / elBBox.height;

    const parent = el.parentElement;
    const grandparent = parent.parentElement;

    const transforms = [`translate(${this.x}, ${this.y})`];

    if (this.shouldRotate) {
      transforms.push("rotate(90)");
    }

    if (flipped) {
      transforms.push(`translate(0, ${this.boatHeight})`);
    }

    transforms.push(`scale(${scaleX}, ${scaleY})`);

    if (this.shouldRotate) {
      const translateY = (-1 * (flipped ? this.longSide : this.boatHeight)) / scaleY;
      transforms.push(`translate(0, ${translateY})`);
    }

    parent.setAttribute("transform", transforms.join(" "));

    if (this.isPath) {
      // take the more complex approach of setting values based on calculations if using a `<path>`
      grandparent.setAttribute(
        "transform",
        `rotate(${this.angle} 0 0)`,
      );
      grandparent.setAttribute("transform-origin", `${this.x} ${this.y}`);
    } else {
      // otherwise just copy the transform property off of the `<rect>` element
      grandparent.setAttribute(
        "transform",
        this.rectTarget.getAttribute("transform"),
      );
    }

    if (shouldLog) {
      grandparent.dataset.valueLogs = JSON.stringify({
        x: this.x,
        y: this.y,
        width: this.width,
        height: this.boatHeight,
        angle: this.angle,
        shouldShowBoat: this.shouldShowBoat,
        boatHeight: this.boatHeight,
        flipped,
        scaleX,
        scaleY,
      });
    }

    return true;
  }

  /* eslint-disable class-methods-use-this */
  unselectAllBerths() {
    const berthEls = document.querySelectorAll("[data-controller=berth-path]");
    berthEls.forEach((e) => {
      e.dataset.berthPathSelectedValue = false;
    });
  }

  select() {
    this.selectedValue = true;
  }

  get path() {
    return this.element.querySelector(":is(path, rect)");
  }

  get isPath() {
    /*
    the map can either support berths marked as
    `<path>` or `<rect>` elements. this getter gives a boolean
    of which one is being used. this is important because
    it significantly modifies the calculations that need to
    be done. in general, the `<path>` calculations are
    noticeably more complex, whereas the `<rect>`
    calculations can preserve more of what's on the
    screen by default.
    */
    return Boolean(this.path.tagName.match(/path/i));
  }

  get screenReaderLabel() {
    // Get i18n descriptions from parent controller
    const berthMapController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="berth-map"]'),
      "berth-map",
    );
    const variantDescriptions = berthMapController?.variantDescriptionsValue || {};

    const statusDescription = BerthDisplayVariants.getVariantDescription(
      this.displayVariant,
      variantDescriptions,
    );

    return `Berth in pontoon ${this.berthValue.pontoon} and position number ${this.berthValue.pontoon_number}. Status: ${statusDescription}.`;
  }

  get className() {
    return BerthDisplayVariants.getCSSClasses(this.displayVariant, "map");
  }

  get boatIconTarget() {
    return document.querySelector("[data-berth-map-target=boat] path");
  }

  get pathData() {
    return this.path.getPathData();
  }

  get x() {
    // get all the x coords and find the min
    if (this.isPath) {
      const xCoords = this.pathData.map((d) => d.values[0]).filter(Boolean);
      return xCoords[0];
    }

    return this.path.x.baseVal.value;
  }

  get y() {
    // get all the y coords and find the min
    if (this.isPath) {
      const yCoords = this.pathData.map((d) => d.values[1]).filter(Boolean);
      return yCoords[0];
    }

    return this.path.y.baseVal.value;
  }

  get heights() {
    // calculate the heights of all 4 lines in the path
    if (!this.isPath) return [];

    return this.pathData
      .map((point1, index) => {
        const point2 = this.pathData[index + 1];
        if (!point2) return undefined;

        return Math.sqrt(
          (point1.values[0] - point2.values[0]) ** 2
          + (point1.values[1] - point2.values[1]) ** 2,
        );
      })
      .filter(Boolean);
  }

  get width() {
    /*
    calculate the height of all paths and find the min
    (we assume the retangle should be longer than it is wide)
    */
    if (this.isPath) return Math.min(...this.heights) || 0;
    return this.path.width.baseVal.value;
  }

  get height() {
    /*
    calculate the height of all paths and find the
    max (we assume the retangle should be longer than it is wide)
    but if a boat is booked in, we actually want
    to scale the height to the length of that boat!
    */
    if (this.isPath) return Math.max(...this.heights) || 0;
    return this.path.height.baseVal.value || 0;
  }

  get longSide() {
    // the long side is the side that is longer than the other two sides
    return Math.max(this.width, this.height);
  }

  get shortSide() {
    // the short side is the side that is shorter than the other two sides
    return Math.min(this.width, this.height);
  }

  get boatHeight() {
    if (!this.shouldShowBoat) return this.longSide;

    const boatLength = this.mapCalendarEntryValue.booked_boat_length_in_cm;
    const berthLength = this.berthValue.length_in_cm;
    return (boatLength / berthLength) * this.longSide;
  }

  get angle() {
    if (this.isPath) {
      const dy = this.pathData[3].values[1] - this.pathData[0].values[1];
      const dx = this.pathData[0].values[0] - this.pathData[3].values[0];
      const theta = Math.atan2(dy, dx); // range (-PI, PI]

      return 90 - (theta * 180) / Math.PI;
    }

    return (
      /* eslint-disable no-useless-escape */
      parseFloat(
        this.path
          .getAttribute("transform")
          ?.match(/rotate\((-?[\d\.]*)\)/)?.[1],
        10,
      ) || 0
      /* eslint-enable no-useless-escape */
    );
  }

  get translate() {
    if (this.isPath) {
      return [];
    }

    const matchData = this.path
      .getAttribute("transform")
      ?.match(/translate\((-?[\d.]*)\s?(-?[\d.]*)\)/);
    return matchData
      ? [parseFloat(matchData[1], 10), parseFloat(matchData[2], 10)]
      : [0, 0];
  }

  get shouldRotate() {
    /*
    sometimes if the original rectangle was of a
    different orientation than the one we're using, we
    need to rotate the boat icon
    */
    if (this.isPath) return false;
    return this.width > this.height;
  }

  get shouldShowBoat() {
    return BerthDisplayVariants.isBoatVariant(this.displayVariant);
  }
  /* eslint-enable class-methods-use-this */
}
/* eslint-enable no-unused-expressions */
